import { createContext, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  listFields,
  listMembers,
  canEditTeamIds,
  listObjectsFields,
  getIdeaPageDisplay,
  listOwners,
  getSettingsForAccount,
} from "../Api";
import { ChildProps, IdeaPriority, syncContextType } from "../types/types";
import { addOptionsToFilters } from "./helpers";
import { fetchDomainToggle } from "../api/domains"
import { getTags } from "../api/tags";
import { getDeliveryPlatformType } from "../api/integrations";
import { EncryptStorage } from "encrypt-storage";

// Create encrypted storage instance for caching only the fields data
// Use a strong secret key (in production this should come from environment variables)
const encryptedStorage = new EncryptStorage("bagel-app-secret-key-2024", {
  prefix: "@bagel-cache",
  storageType: "localStorage",
  encAlgorithm: "AES" // Advanced Encryption Standard
});

// Cache TTL in milliseconds (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

const initialState: syncContextType = {
  account: {},
  fieldsLoaded: false,
  deliveryPlatform: null,
  fields: null,
  components: null,
  ideaStatuses: null,
  ideaPriorities: null,
  requestPriorities: null,
  logout: null,
  members: null,
  owners: null,
  objectCustomizations: null,
  editCustomizations: null,
  defaultPriority: null,
  defaultStatus: null,
  getFields: null,
  priorityData: null,
  domains: [],
  requestPrioritiesColors: null,
  defaultRequestPriority: null,
  accountAdditionalInfo: null,
  opportunityAdditionalInfo: null,
  showDomain: false,
  evidenceDomain: false,
  displayState: null,
  enableEditOnImportedIdeas: false,
  showWhatsNewBanner: null,
  enableIdeaValidation: false,
  allComponents: [],
  setAllComponents: () => ({}),
  businessCategoryMapping: null,
  currentAccount: null,
  ideasTableDisplay: null,
  customEvidenceObj: null,
  accountDoc: null,
  allAggregationProperties: [],
  impactAggregationProperties: [],
  perspectiveConfigurations: [],
  ideaColumns: [],
  subPages: [],
  ideaDefaultSortColumn: null,
  setIdeasFiltersConfig: null,
  ideasFiltersConfig: {
    evidence: {},
    idea: {},
  },
  markedDeliveryStatus: [],
  setMarkedDeliveryStatus: null,
  ideaInfoTabsConfig: {},
  addedAttributes: [],
  tagsList: [],
  tagsOptions: [],
  discoveryEvidencesSources: [],
  fetchTags: () => ({}),
  gtmAppsConfiguration: {},
  generatedEnabled: false,
  threeSixtyColumns: [],
  threeSixtyEnabled: false,
  threeSixtyConfig: {},
  setThreeSixtyConfig: () => ({}),
  allColumns: [],
  setAllColumns: () => ({}),
  setFields: () => ({})
};

interface DomainProps {
  accountId: string;
  name: string;
  value: string;
  _id: string;
}
interface DisplayState {
  [value: string]: boolean;
}

const SyncContext = createContext<syncContextType>(initialState);

const SyncContextProvider = ({ children }: ChildProps) => {
  const auth0 = useAuth0();

  const [account, setAccount] = useState({});
  const [fieldsLoaded, setFieldsLoaded] = useState<boolean>(false);
  
  // Only use direct state initialization for fields, revert other properties to default state
  const [deliveryPlatform, setDeliveryPlatform] = useState<any>(null);
  
  // Try to get cached fields data
  const cachedFields = getCachedFields();
  const [fields, setFields] = useState<any>(cachedFields);
  
  const [currentAccount, setCurrentAccount] = useState<any>(null);
  const [showDomain, setShowDomain] = useState<boolean>(false);
  const [enableEditOnImportedIdeas, setEnableEditOnImportedIdeas] = useState<boolean>(false);
  const [showWhatsNewBanner, setShowWhatsNewBanner] = useState<boolean | null>(null);
  const [enableIdeaValidation, setEnableIdeaValidation] = useState<boolean>(false);
  const [components, setComponents] = useState<any[]>([]);
  const [allColumns, setAllColumns] = useState<any[]>([]);
  const [allComponents, setAllComponents] = useState<any[]>([]);
  const [ideaStatuses, setIdeaStatuses] = useState<any>(null);
  const [ideaPriorities, setIdeaPriorities] = useState<any>(null);
  const [requestPriorities, setRequestPriorities] = useState<any[]>([]);
  const [priorityData, setPriorityData] = useState<any[]>([]);
  const [members, setMembers] = useState([]);
  const [owners, setOwners] = useState([]);
  const [editCustomizations, setEditCustomizations] = useState<any>();
  const [objectCustomizations, setObjectCustomizations] = useState<any>();
  const [defaultPriority, setDefaultPriority] = useState<any>();
  const [defaultStatus, setDefaultStatus] = useState<any>();
  const [domains, setDomains] = useState<DomainProps[]>([]);
  const [requestPrioritiesColors, setRequestPrioritiesColors] = useState<any>(
    {}
  );
  const [defaultRequestPriority, setDefaultRequestPriority] = useState<any>();
  const [accountAdditionalInfo, setAccountAdditionalInfo] = useState<any>(null);
  const [opportunityAdditionalInfo, setOpportunityAdditionalInfo] =
    useState<any>(null);
  const [evidenceDomain, setEvidenceDomain] = useState<boolean>(false);
  const [displayState, setDisplayState] = useState<DisplayState>({});
  const [businessCategoryMapping, setBusinessCategoryMapping] = useState<any[]>(
    []
  );
  const [allAggregationProperties, setAllAggregationProperties] = useState<
    any[]
  >([]);




  const [impactAggregationProperties, setAgregationProperties] = useState<
        any[]
    >([]);
  const [perspectiveConfigurations, setPerspectiveConfigurations] = useState<
    any[]
  >([]);
  const [ideaColumns, setIdeaColumns] = useState<any[]>([]);
  const [subPages, setSubPages] = useState<any[]>([]);
  const [ideaDefaultSortColumn, setIdeaDefaultSortColumn] = useState<string | null>(null);
  const [ideaInfoTabsConfig, setIdeaInfoTabsConfig] = useState<any>({});
  const [ideasFiltersConfig, setIdeasFiltersConfig] = useState<{
    evidence: any;
    idea: any;
  }>({
    evidence: {},
    idea: {},
  });

  const [markedDeliveryStatus, setMarkedDeliveryStatus] = useState<any[]>([]);

  const [ideasTableDisplay, setIdeasTableDisplay] = useState<any>({
    pendingSuggestions: false,
    customEvidenceTypes: false,
  });
  const [customEvidenceObj, setCustomEvidenceObj] = useState();
  const [accountDoc, setAccountDoc] = useState();
  const [tagsList, setTagsList] = useState<any[]>([]);
  const [tagsOptions, setTagsOptions] = useState<any[]>([]);
  const [addedAttributes, setAddedAttributes] = useState<any[]>([]);
  const [discoveryEvidencesSources, setDiscoveryEvidencesSources] = useState<any[]>([]);
  const [gtmAppsConfiguration, setGtmAppsConfiguration] = useState<any | null>(null);
  const [generatedEnabled, setGeneratedEnabled] = useState<boolean>(false);
  const [threeSixtyColumns, setThreeSixtyColumns] = useState<any[]>([]);
  const [threeSixtyEnabled, setThreeSixtyEnabled] = useState<boolean>(false);
  const [threeSixtyConfig, setThreeSixtyConfig] = useState<any>({});

  /* console.log('threeSixtyConfig', threeSixtyConfig); */

  /* console.log('threeSixtyEnabled', threeSixtyEnabled) */

  
  // Helper function to get cached fields with TTL check
  function getCachedFields() {
    try {
      const cachedData = encryptedStorage.getItem('fields');
      
      if (!cachedData) return null;
      
      const { data, timestamp } = cachedData;
      const now = Date.now();
      
      // Return null if cache is expired
      if (now - timestamp > CACHE_TTL) {
        return null;
      }
      
      return data;
    } catch (e) {
      console.warn('Failed to retrieve cached fields:', e);
      return null;
    }
  }
  
  // Helper function to cache fields data
  function cacheFields(data: any) {
    try {
      encryptedStorage.setItem('fields', {
        data,
        timestamp: Date.now()
      });
    } catch (e) {
      console.warn('Failed to cache fields:', e);
    }
  }
  
  const logout = () => {
    localStorage.clear();
  };

  const getDefaultpriorityAndStatus = async () => {
    const priorityRes = await listObjectsFields("priority", auth0);
    const priorityDefault = priorityRes?.options.find(
      (o: any) => o.isDefault === true
    );
    setDefaultPriority(priorityDefault?.key ?? "");
    const statusRes = await listObjectsFields("status", auth0);
    const statusDefault = statusRes?.options.find(
      (o: any) => o.isDefault === true
    );
    setDefaultStatus(statusDefault?.key ?? "");
  };

  const loadDomainEvidenceDisplay = () => {
    getIdeaPageDisplay("evidence", auth0)
      .then((res) => {
        setEvidenceDomain(res.domain);
      })
      .catch((e) => console.log(e));
  };

  // console.log({objectCustomizations})

  const getFields = () => {
    // If we have cache and it's not set in the state yet
    const cachedFields = getCachedFields();
    if (cachedFields && !fields) {
      setFields(cachedFields);
      setFieldsLoaded(true);
    } else {
      setFieldsLoaded(false);
    }
    
    listFields(auth0)
      .then((res) => {
        setFields(res);
        // Cache the fields data
        cacheFields(res);
        setEnableIdeaValidation(res.enableIdeaValidation);
        
        if (res.flags?.length > 0) {
          res.flags.forEach((flag: any) => {
            if (flag.name === "enableEditOnImportedIdeas" && typeof flag.value === 'boolean') {
              setEnableEditOnImportedIdeas(flag.value);
            }  
            if (flag.name === "enableIdeaValidation" && typeof flag.value === 'boolean') {
              setEnableIdeaValidation(flag.value);
            }
          })
        }

        setShowWhatsNewBanner(res.showWhatsNewBanner);
        setAllColumns(res.ideaColumnsConfig || []);
        setComponents(res.components || []);
        setDomains(res.domains || []);
        const sortedStatuses = res.ideaStatuses.sort(
          (a: any, b: any) => a.index - b.index
        );
        setIdeaStatuses(sortedStatuses || []);
        const sortedIdeaPriorities = res.ideaPriorities.sort(
          (a: any, b: any) => a.index - b.index
        );
        setIdeaPriorities(sortedIdeaPriorities || IdeaPriority);
        const sortedRequestPriorities = res.requestPriorities.sort(
          (a: any, b: any) => a.index - b.index
        );
        setRequestPriorities(sortedRequestPriorities || []);
        setObjectCustomizations(res?.customizations || {});
        setCustomEvidenceObj(res?.customizations?.customEvidenceObj || {});
        setAllComponents(res.allComponents || []);
        setAgregationProperties(res.impactAggregationProperties || []);
        setAllAggregationProperties(res.allAggregationProperties || []);
        setPerspectiveConfigurations(res.perspectiveConfigurations || []);
        setIdeaColumns(res.enabledColoumns || []);
        setSubPages(res.subPages || []);
        setDiscoveryEvidencesSources(res.discoveryEvidencesSources || []);
        setIdeaInfoTabsConfig(res.ideaInfoTabsConfig || {});
        setAddedAttributes(res.addedAttributes || []);
        setIdeasFiltersConfig(res.ideasFiltersConfig || {});
        setIdeaDefaultSortColumn(res.defaultSortColumn);
        // console.log({["------ideaInfoTabsConfig:"]: res.ideaInfoTabsConfig})
        setIdeaInfoTabsConfig(res.ideaInfoTabsConfig || {});
        setAddedAttributes(res.addedAttributes || []);
        setIdeasFiltersConfig(res.ideasFiltersConfig || {});
        // console.log({ideasFiltersConfig})
        setMarkedDeliveryStatus(res.deliveryStatus || []);
        setGtmAppsConfiguration(res?.gtmAppsConfiguration || null);
        setGeneratedEnabled(res?.generatedEnabled || false)
        if(res?.threeSixtyConfiguration?.colomns){
          const customerColoumns = res?.threeSixtyConfiguration?.colomns
          const sortedColoumns = customerColoumns.sort((a: any, b: any) => (a?.index || 0) - (b?.index || 0))
          setThreeSixtyColumns(sortedColoumns || []);
        }
        
        setThreeSixtyConfig(res?.threeSixtyConfiguration || {});
        if (res?.threeSixtyConfiguration?.threeSixtyEnabled) {
          setThreeSixtyEnabled(true)
        }
        const accountFields = res?.customizations?.account?.fields || null;
        const accountAdditionalInfoObj: any = {};
        if (accountFields) {
          for (const k in accountFields) {
            if (accountFields[k].isExtra) {
              accountAdditionalInfoObj[k] = accountFields[k];
            }
          }
        }
        setAccountAdditionalInfo(accountAdditionalInfoObj);
        setAccountDoc(res?.accountDoc);

        const opportunityFields =
          res?.customizations?.opportunity?.fields || null;
        const opportunityAdditionalInfoObj: any = {};
        if (opportunityFields) {
          for (const k in opportunityFields) {
            if (opportunityFields[k].isExtra) {
              opportunityAdditionalInfoObj[k] = opportunityFields[k];
            }
          }
        }
        setOpportunityAdditionalInfo(opportunityAdditionalInfoObj);
        const priorities = res.requestPriorities;
        const requestPriorityDefault = priorities?.find(
          (o: any) => o.isDefault === true
        );
        setDefaultRequestPriority(requestPriorityDefault.label);

        if (res.requestPriorities) {
          const copy = [...res.requestPriorities];
          const priorities = copy.map((r: any) => r.label);
          setPriorityData(priorities ?? []);
          const secondCopy = [...res.requestPriorities];

          const priorityColorObject: any = {};
          secondCopy.forEach((s: any) => {
            priorityColorObject[s.label] = s.color || s.chartColor;
          });

          setRequestPrioritiesColors(priorityColorObject);
        }

        if (res.currentAccount) {
          setCurrentAccount(res.currentAccount);
        }
        setBusinessCategoryMapping(
          res.businessCategoryMapping[0]?.businessNames || []
        );
        setFieldsLoaded(true);
      })
      .catch((e) => {
        console.log(e);
        // If fetch fails and we have cached fields data, continue using it
        if (cachedFields) {
          setFieldsLoaded(true);
        }
      });
  };
  const loadAccount = () => {

    getSettingsForAccount(auth0)
      .then(account => {
        setAccount(account)
      
      })
      .catch((error) => console.log(error));
  };

  const loadCanEditTeamIds = () => {
    canEditTeamIds(auth0)
      .then((customizations: any) => {
        setEditCustomizations(
          customizations.reduce((acc: any, customization: any) => {
            return {
              ...acc,
              [customization.name]: customization.value,
            };
          }, {})
        );
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const loadMembers = () => {
    listMembers(auth0)
      .then((users) => {
        setMembers(users);
      })
      .catch((e) => console.log(e));
  };

  const loadOwners = () => {
    listOwners(auth0)
      .then((users) => {
        setOwners(users);
      })
      .catch((e) => console.log(e));
  };

  const fetchDomainToggleFn = async () => {
    await fetchDomainToggle(auth0).then((res) => {

      const toggle = res.toggle?.length > 0 ? res.toggle[0].useDomains : false;
      setShowDomain(toggle);
    });
  };

  const loadIdeaPageDisplay = () => {
    getIdeaPageDisplay("idea", auth0)
      .then((res) => {
        setDisplayState(res);
      })
      .catch((e) => console.log(e));
  };

  const loadIdeasTableDisplay = () => {
    getIdeaPageDisplay("ideas", auth0)
      .then((res) => {
        setIdeasTableDisplay(res);
      })
      .catch((e) => console.log(e));
  };

  // Fetch all tags to populate options
  const fetchTags = async (): Promise<void> => {
    try {
      const tagsList = await getTags(auth0);
      setTagsList((tagsList || []).sort((a: any, b: any) => a.index - b.index));
      
      // Map tags to options format
      const options = tagsList
        .sort((a: any, b: any) => a.index - b.index)
        .map((tag: any) => ({
          value: tag._id,
          index: tag.index,
          label: tag.label,
          color: tag.color,
        }))
        .sort((a: any, b: any) => (a?.index || 0) - (b?.index || 0));
      setTagsOptions(options);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  useEffect(() => {
    if (members.length > 0 || owners.length > 0) {
      const updatedFilters = addOptionsToFilters(ideasFiltersConfig,
        {
          members,
          owners,
          components,
          tagsOptions
        });
      setIdeasFiltersConfig(updatedFilters);
    }
  }, [fields, members, owners]);

  useEffect(() => {
    loadIdeasTableDisplay();
    loadIdeaPageDisplay();
    getFields();
    loadOwners();
    loadMembers();
    loadCanEditTeamIds();
    getDefaultpriorityAndStatus();
    fetchDomainToggleFn();
    loadDomainEvidenceDisplay();
    fetchTags();
    getDeliveryPlatformType(auth0).then((res) => {
      if (res) {
        setDeliveryPlatform(res);
      }
    }).catch(e => console.log(e));
    loadAccount()
  }, []);

  return (
    <>
      <SyncContext.Provider
        value={{
          account,
          fieldsLoaded,
          deliveryPlatform,
          fields,
          setFields,
          components,
          ideaStatuses,
          ideaPriorities,
          logout,
          members,
          owners,
          objectCustomizations,
          editCustomizations,
          defaultPriority,
          defaultStatus,
          enableEditOnImportedIdeas,
          showWhatsNewBanner,
          getFields,
          requestPriorities,
          priorityData,
          domains,
          requestPrioritiesColors,
          defaultRequestPriority,
          accountAdditionalInfo,
          opportunityAdditionalInfo,
          showDomain,
          evidenceDomain,
          displayState,
          allComponents,
          setAllComponents,
          businessCategoryMapping,
          currentAccount,
          ideasTableDisplay,
          customEvidenceObj,
          accountDoc,
          allAggregationProperties,
          impactAggregationProperties,
          perspectiveConfigurations,
          ideaColumns,
          subPages,
          ideaDefaultSortColumn,
          ideasFiltersConfig,
          setIdeasFiltersConfig,
          markedDeliveryStatus,
          setMarkedDeliveryStatus,
          ideaInfoTabsConfig,
          addedAttributes,
          tagsList,
          tagsOptions,
          discoveryEvidencesSources: discoveryEvidencesSources,
          fetchTags,
          gtmAppsConfiguration,
          generatedEnabled,
          enableIdeaValidation,
          threeSixtyColumns,
          threeSixtyEnabled,
          threeSixtyConfig,
          setThreeSixtyConfig,
          allColumns,
          setAllColumns
        }}
      >
        {children}
      </SyncContext.Provider>
    </>
  );
};

export { SyncContext, SyncContextProvider };
export default SyncContext;
